import type {
  GrantDraftExportFormat,
  GrantOpportunityDocumentLink,
} from "@/api/grant_opportunity_documents";

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function draftsPath(municipalityId: number, opportunityId: number, linkId?: number): string {
  const base = `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/application_drafts`;
  return linkId ? `${base}/${linkId}` : base;
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}

export function exportApplicationDraftUrl(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
): string {
  return `${draftsPath(municipalityId, opportunityId, linkId)}/export`;
}

export type ApplicationDraftPayload = {
  markdown: string;
  label?: string;
};

export type ApplicationDraftWithMarkdown = GrantOpportunityDocumentLink & {
  markdown: string;
};

export async function generateGrantApplicationMarkdown(
  grantId: number,
  municipalityId: number,
): Promise<string> {
  const response = await fetch("/api/v1/completions/generate_grant_pdf_markdown", {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      generate_grant_pdf: {
        grant_id: grantId,
        municipality_id: municipalityId,
      },
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error ?? "Could not generate application draft");
  }
  return data.response ?? "";
}

export async function saveApplicationDraft(
  municipalityId: number,
  opportunityId: number,
  payload: ApplicationDraftPayload,
): Promise<GrantOpportunityDocumentLink> {
  const response = await fetch(draftsPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      application_draft: {
        markdown: payload.markdown,
        label: payload.label ?? null,
      },
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Failed to save application draft");
  }
  return data;
}

export async function fetchApplicationDraft(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
): Promise<ApplicationDraftWithMarkdown> {
  const response = await fetch(draftsPath(municipalityId, opportunityId, linkId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Failed to load application draft");
  }
  return data;
}

export async function updateApplicationDraft(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
  payload: ApplicationDraftPayload,
): Promise<GrantOpportunityDocumentLink> {
  const response = await fetch(draftsPath(municipalityId, opportunityId, linkId), {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      application_draft: {
        markdown: payload.markdown,
        label: payload.label ?? null,
      },
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.errors?.join(", ") ?? data.error ?? "Failed to update application draft");
  }
  return data;
}

export async function downloadApplicationDraft(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
  format: GrantDraftExportFormat = "docx",
): Promise<void> {
  const response = await fetch(
    `${exportApplicationDraftUrl(municipalityId, opportunityId, linkId)}?format=${format}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw new Error("Failed to export application draft");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
    `application-draft-${linkId}.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
